import { Plus, XClose } from '@untitledui/icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Input } from '@/lib/base/forms/input/input';
import { PropertyInput } from '@/lib/crm/components/properties/PropertyInput';
import type { OperatorType } from '@/lib/crm/filters/types';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import type { FilterValue } from '@/types/crm';

import {
  isMultiTextOperator,
  isSubstringOperator,
  MULTI_SELECT_CLASS_NAME,
  needsMultiSelect,
} from './shared';

interface PropertyValueInputProps {
  property: PropertyTypeSchema;
  operator: OperatorType;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
}

/**
 * Value input for a property-typed filter clause: a substring text input, a
 * multi-select, or the canonical PropertyInput depending on operator/property.
 * Shared by the property path and the associated-property path so both render
 * identically. Callers gate on needsValueInput(operator) before rendering.
 */
export function PropertyValueInput({
  property,
  operator,
  value,
  onChange,
}: PropertyValueInputProps) {
  const handleMultiSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const values = Array.from(e.target.selectedOptions).map(o => o.value);
      onChange(values.join(','));
    },
    [onChange]
  );

  const handlePropertyValueChange = useCallback(
    (newValue: unknown) => onChange(newValue as FilterValue),
    [onChange]
  );

  if (isSubstringOperator(operator)) {
    return (
      <Input
        type='text'
        value={typeof value === 'string' ? value : ''}
        onChange={onChange}
        icon={getPropertyTypeIconComponent(property)}
        size='sm'
        aria-label={property.label}
        placeholder={property.label}
      />
    );
  }

  if (isMultiTextOperator(operator)) {
    return (
      <MultiTextValueInput
        value={value}
        onChange={onChange}
        property={property}
      />
    );
  }

  if (needsMultiSelect(operator, property)) {
    return (
      <select
        className={MULTI_SELECT_CLASS_NAME}
        multiple
        value={typeof value === 'string' ? value.split(',') : []}
        onChange={handleMultiSelectChange}
        aria-label={`${property.label} filter values`}
      >
        {(property.options || []).map(
          (option: { value: string; label: string }) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          )
        )}
      </select>
    );
  }

  const scalarProperty =
    property.is_multi === true ? { ...property, is_multi: false } : property;

  let scalarValue: FilterValue = value;
  if (Array.isArray(scalarValue)) {
    scalarValue = scalarValue.length > 0 ? (scalarValue[0] ?? null) : null;
  }

  let inputValue: string | number | object;
  if (scalarValue === null || scalarValue === undefined) {
    inputValue = '';
  } else if (typeof scalarValue === 'boolean') {
    inputValue = String(scalarValue);
  } else {
    inputValue = scalarValue as string | number | object;
  }

  return (
    <PropertyInput
      propertyType={scalarProperty}
      value={inputValue}
      onChange={handlePropertyValueChange}
      disabled={false}
    />
  );
}

interface MultiTextRowProps {
  index: number;
  value: string;
  icon?: ReturnType<typeof getPropertyTypeIconComponent>;
  label: string;
  showRemove: boolean;
  onChangeValue: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

function MultiTextRow({
  index,
  value,
  icon,
  label,
  showRemove,
  onChangeValue,
  onRemove,
}: MultiTextRowProps) {
  const handleChange = useCallback(
    (newValue: string) => onChangeValue(index, newValue),
    [index, onChangeValue]
  );

  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);

  return (
    <div className='flex items-center gap-1'>
      <Input
        type='text'
        value={value}
        onChange={handleChange}
        icon={icon}
        size='sm'
        aria-label={`${label} value ${index + 1}`}
        placeholder={label}
      />
      {showRemove && (
        <button
          type='button'
          onClick={handleRemove}
          aria-label={`Remove ${label} value ${index + 1}`}
          className='shrink-0 cursor-pointer rounded-md p-1.5 text-tertiary transition-colors hover:bg-bg-secondary_hover hover:text-primary'
        >
          <XClose className='size-4' />
        </button>
      )}
    </div>
  );
}

interface MultiTextValueInputProps {
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  property: PropertyTypeSchema;
}

/**
 * contains_any/not_contains_any take a list of raw substring fragments — no
 * predefined options, unlike in/not_in on select fields — so each fragment is
 * a free-text row instead of a multi-select.
 */
function MultiTextValueInput({
  value,
  onChange,
  property,
}: MultiTextValueInputProps) {
  const entries = useMemo<string[]>(() => {
    if (Array.isArray(value)) {
      return value.map(v => (typeof v === 'string' ? v : String(v ?? '')));
    }
    if (typeof value === 'string' && value.length > 0) {
      return [value];
    }
    return [''];
  }, [value]);

  // Synthetic ids used only as React keys, index-aligned with `entries` —
  // never merged into the emitted value. Kept in sync with entries.length so
  // an externally-changed value (e.g. flipping the operator onto a
  // pre-existing array) still gets stable-enough keys.
  const [rowIds, setRowIds] = useState<string[]>(() =>
    entries.map(() => crypto.randomUUID())
  );

  useEffect(() => {
    setRowIds(prev => {
      if (prev.length === entries.length) {
        return prev;
      }
      if (prev.length < entries.length) {
        return [
          ...prev,
          ...Array.from({ length: entries.length - prev.length }, () =>
            crypto.randomUUID()
          ),
        ];
      }
      return prev.slice(0, entries.length);
    });
  }, [entries.length]);

  const handleEntryChange = useCallback(
    (index: number, newEntryValue: string) => {
      onChange(
        entries.map((entry, i) => (i === index ? newEntryValue : entry))
      );
    },
    [entries, onChange]
  );

  const handleAdd = useCallback(() => {
    setRowIds(prev => [...prev, crypto.randomUUID()]);
    onChange([...entries, '']);
  }, [entries, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      setRowIds(prev => prev.filter((_, i) => i !== index));
      const next = entries.filter((_, i) => i !== index);
      onChange(next.length > 0 ? next : ['']);
    },
    [entries, onChange]
  );

  const Icon = getPropertyTypeIconComponent(property);

  return (
    <div className='flex w-full flex-col gap-1.5'>
      {entries.map((entry, index) => (
        <MultiTextRow
          key={rowIds[index] ?? index}
          index={index}
          value={entry}
          icon={index === 0 ? Icon : undefined}
          label={property.label}
          showRemove={entries.length > 1}
          onChangeValue={handleEntryChange}
          onRemove={handleRemove}
        />
      ))}
      <button
        type='button'
        onClick={handleAdd}
        className='flex w-fit cursor-pointer items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium text-brand-secondary hover:text-brand-secondary_hover'
      >
        <Plus className='size-3.5' />
        Add value
      </button>
    </div>
  );
}
